import React from 'react'
import { Container, Typography, Box, Grid, useTheme, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import ModernFooter from '../Components/ModernFooter';
import ModernCard from '../Components/ModernCard';

import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerChildren,
  staggerItem,
} from '../styles/animations';

import logo from "../Logo.png";
import photoDG from "../Files/photoDG.png";
import PhotoTechnicien from "../Files/PhotoTechnicien.png";
import PhotoConsultant from "../Files/PhotoConsultant.png";
import photoCommercial from "../Files/photoCommercial.png";
import tiffanyPhoto from "../Files/TIFFANY  PHOTO SITE.jpg";

interface Props { }

function About(props: Props) {
    const { t } = useTranslation();
    const theme = useTheme();

    const teamMembers = [
        {
            name: "Célestin Tsafond",
            role: t("contenu.texte39"),
            image: photoDG,
        },
        {
            name: "Jean Valery Tchouta Noutcha",
            role: t("contenu.texte40"),
            image: PhotoTechnicien,
        },
        {
            name: "Victoire Ntoumsi",
            role: t("contenu.texte41"),
            image: PhotoConsultant,
        },
        {
            name: "Davina Mbialeu",
            role: t("contenu.texte78"),
            image: photoCommercial,
        },
        {
            name: "Tiffany Princesse Ngamche",
            role: "Agente de communication",
            image: tiffanyPhoto,
        },
    ];

    return (
        <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
            {/* Hero Section */}
            <Box 
                sx={{
                    background: 'linear-gradient(135deg, #003d7a 0%, #1e5ba8 50%, #ff6b35 100%)',
                    color: 'white',
                    py: 8,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        variants={fadeInUp}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <Typography
                            variant="h3"
                            component="h1"
                            align="center"
                            gutterBottom
                            sx={{ fontWeight: 700, mb: 3 }}
                        >
                            {t("contenu.texte27")}
                        </Typography>
                        <Typography
                            variant="h6"
                            align="center"
                            sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}
                        >
                            {t("contenu.texte2")}
                        </Typography>
                    </motion.div>
                </Container>
            </Box>

            {/* About Content */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <motion.div
                            variants={fadeInLeft}
                            initial="initial"
                            whileInView="animate"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <Box sx={{ mb: 4 }}>
                                <Avatar
                                    src={logo}
                                    alt="NKH Construction Logo"
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mx: 'auto',
                                        mb: 3,
                                        border: '4px solid',
                                        borderColor: 'primary.main',
                                    }}
                                />
                                <Typography
                                    variant="h4"
                                    component="h2"
                                    gutterBottom
                                    sx={{ fontWeight: 600, textAlign: 'center' }}
                                >
                                    {t("contenu.texte28")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ lineHeight: 1.7, textAlign: 'center' }}
                                >
                                    {t("contenu.texte29")}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <motion.div
                            variants={fadeInRight}
                            initial="initial"
                            whileInView="animate"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="h4"
                                    component="h2"
                                    gutterBottom
                                    sx={{ fontWeight: 600, color: 'primary.main' }}
                                >
                                    {t("contenu.texte30")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ lineHeight: 1.7, mb: 3 }}
                                >
                                    {t("contenu.texte3")}
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>{t("contenu.texte31")}:</strong> {t("contenu.texte32")}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>{t("contenu.texte33")}:</strong> {t("contenu.texte34")}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>{t("contenu.texte35")}:</strong> {t("contenu.texte36")}
                                    </Typography>
                                </Box>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>

                {/* Team Section */}
                <Box sx={{ mt: 8 }}>
                    <motion.div
                        variants={fadeInUp}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <Typography
                            variant="h3"
                            component="h2"
                            align="center"
                            gutterBottom
                            sx={{ fontWeight: 700, mb: 2 }}
                        >
                            {t("contenu.texte37")}
                        </Typography>
                        <Typography
                            variant="h6"
                            align="center"
                            color="text.secondary"
                            sx={{ mb: 6 }}
                        >
                            {t("contenu.texte38")}
                        </Typography>
                    </motion.div>
                    
                    <motion.div
                        variants={staggerChildren}
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <Grid container spacing={4}>
                            {teamMembers.map((member, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <motion.div variants={staggerItem}>
                                        <ModernCard
                                            variant="testimonial"
                                            image={member.image}
                                            title={member.name}
                                            description={member.role}
                                            linkText=""
                                        />
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Box>
            </Container>
            
            <ModernFooter />
        </Box>
    )
}

export default About